import { useEffect, useRef } from 'react';

import { activityApi } from '@/core/api';
import { useAuth } from '@/core/auth';

const DEVICE_KEY_STORAGE = 'itow-device-key';

/** How often presence is reported. */
const HEARTBEAT_MS = 60_000;

/** How often the visible-seconds counter ticks. */
const TICK_MS = 1_000;

/**
 * A stable id for this browser.
 *
 * Not a person and not a session — a person on a laptop and the same person on
 * a phone should be two sittings rather than one flickering between platforms,
 * and a reconnect should extend the session it belongs to rather than open a
 * new one.
 */
export function deviceKey(): string {
  const existing = localStorage.getItem(DEVICE_KEY_STORAGE);
  if (existing !== null && existing !== '') return existing;
  const key =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `d${Date.now()}${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(DEVICE_KEY_STORAGE, key);
  return key;
}

/**
 * Reports that this person is using the app.
 *
 * **Counts visible seconds, not elapsed ones.** A tab left open behind a
 * spreadsheet all afternoon is not four hours of testing, and counting wall
 * clock would make the one figure this exists to produce worthless. The counter
 * only advances while the document is visible.
 *
 * **Sends running totals, never deltas.** Heartbeats arrive late, twice and out
 * of order, and the server keeps the highest total it has been told — which
 * makes every one of those cases harmless instead of corrupting.
 *
 * **Silent on failure, always.** Measuring whether somebody used the app must
 * never be the reason they could not.
 */
export function useActivityHeartbeat(): void {
  const { isAuthenticated, currentUser } = useAuth();
  const secondsRef = useRef(0);

  // Read through a ref so a token refresh that produces a new user object does
  // not restart the effect and reset the count.
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = currentUser?.userId ?? null;

  useEffect(() => {
    if (!isAuthenticated) return;

    const key = deviceKey();
    let stopped = false;

    const report = () => {
      if (stopped) return;
      void activityApi
        .heartbeat({
          platform: 'WEB',
          deviceKey: key,
          activeSeconds: Math.round(secondsRef.current),
          appVersion: null,
          deviceInfo: navigator.userAgent,
        })
        .catch(() => undefined);
    };

    // One immediately, so opening the app and closing it a minute later still
    // registers as having been opened.
    report();

    const tick = window.setInterval(() => {
      if (document.visibilityState === 'visible') secondsRef.current += TICK_MS / 1000;
    }, TICK_MS);

    const beat = window.setInterval(report, HEARTBEAT_MS);

    // On the way out, so the last minute is not lost. Fired on hide as well as
    // pagehide, because a phone browser being switched away from may never send
    // anything else.
    const onHide = () => {
      if (document.visibilityState === 'hidden') report();
    };
    window.addEventListener('pagehide', report);
    document.addEventListener('visibilitychange', onHide);

    return () => {
      stopped = true;
      window.clearInterval(tick);
      window.clearInterval(beat);
      window.removeEventListener('pagehide', report);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [isAuthenticated]);
}
